//
// # ConstraintDialog
//
// Modal dialog for editing a constraint match.
// UI adapts based on constraint type:
// - Differential/Cross-coupled: single instance dropdowns per side
// - Current Mirror/Matched Load: lists with add/remove functionality
//

import React, { useCallback, useEffect, useState } from "react";
import { Result } from "CoreUtils";
import type { schema } from "GenAlphaSchema";

import { Modal } from "../Modal/Modal";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { createRequestContext } from "../../contextHelpers";
import { SingleInstanceSelector } from "./SingleInstanceSelector";
import { MultiInstanceSelector } from "./MultiInstanceSelector";
import { useConstraints } from "./useConstraints";

// Constraint type options - matches InferredConstraint.constraintType in symmetry_inference.ts
const CONSTRAINT_TYPES = [
  { value: "differential", label: "Differential" },
  { value: "current_mirror", label: "Current Mirror" },
  { value: "matched_load", label: "Matched Load" },
  { value: "cross_coupled", label: "Cross Coupled" },
];

// Types that use single-instance selection (one dropdown per side)
const SINGLE_INSTANCE_TYPES = new Set(["differential", "cross_coupled"]);

export interface ConstraintDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function ConstraintDialog({ open, onClose }: ConstraintDialogProps) {
  const workspace = useWorkspace();
  const { constraintDialog } = useWorkspaceSnapshot();

  // Form state
  const [description, setDescription] = useState("");
  const [constraintType, setConstraintType] = useState("");
  // Stored as string to match HTML select value (always string)
  const [selectedGroupId, setSelectedGroupId] = useState<string>("new");
  const [newGroupName, setNewGroupName] = useState("");
  const [firstInstances, setFirstInstances] = useState<string[]>([]);
  const [secondInstances, setSecondInstances] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available constraint groups
  const { constraints: currentConstraints } = useConstraints();
  const availableGroups = currentConstraints?.groups ?? [];

  // Instance data state - loaded async for layouts (need to fetch linked schematic)
  const [availableInstances, setAvailableInstances] = useState<string[]>([]);
  const [instanceTypeMap, setInstanceTypeMap] = useState<Map<string, string>>(
    new Map()
  );

  // Load instance names and symbol types when dialog opens
  useEffect(() => {
    if (!open) return;

    const loadInstances = async () => {
      const editor = workspace.getActiveEditor();
      if (!editor) {
        setAvailableInstances([]);
        setInstanceTypeMap(new Map());
        return;
      }

      const content = editor.content;

      if (content.contentKind === "schematic") {
        // Read directly from schematic instances
        const instances = [...content.instances];
        const names = instances.map((inst) => inst.data.name).sort();
        const typeMap = new Map<string, string>();
        for (const inst of instances) {
          if (inst.data.symbolType) {
            typeMap.set(inst.data.name, inst.data.symbolType);
          }
        }
        setAvailableInstances(names);
        setInstanceTypeMap(typeMap);
        return;
      }

      if (content.contentKind === "layout") {
        // For layouts, fetch the linked schematic to get symbol types
        const layoutContent = content as { schematicImpl?: schema.Ident };
        const schematicImpl = layoutContent.schematicImpl;

        if (!schematicImpl) {
          setAvailableInstances([]);
          setInstanceTypeMap(new Map());
          return;
        }

        // Fetch the linked schematic
        const ctx = createRequestContext(workspace.userId);
        const schResult = await workspace.client.request(ctx, {
          kind: "open",
          data: { kind: "ident", ident: schematicImpl },
        });

        if (schResult.err) {
          setAvailableInstances([]);
          setInstanceTypeMap(new Map());
          return;
        }

        // Extract instances from the schematic
        const schData = schResult.val as {
          instances?: Array<{ name: string; symbolType?: string }>;
        };
        const instances = schData.instances ?? [];
        const names = instances.map((inst) => inst.name).sort();
        const typeMap = new Map<string, string>();
        for (const inst of instances) {
          if (inst.symbolType) {
            typeMap.set(inst.name, inst.symbolType);
          }
        }
        setAvailableInstances(names);
        setInstanceTypeMap(typeMap);
        return;
      }

      setAvailableInstances([]);
      setInstanceTypeMap(new Map());
    };

    void loadInstances();
  }, [open, workspace]);

  // Format instance name with optional symbol type
  const formatInstance = (name: string): string => {
    const symbolType = instanceTypeMap.get(name);
    return symbolType ? `${name} (${symbolType})` : name;
  };

  // Reset form only when dialog opens or closes — not on every snapshot change.
  // Using `open` as the only dependency prevents mid-edit resets from snapshot updates.
  useEffect(() => {
    if (open && constraintDialog?.constraint) {
      const c = constraintDialog.constraint;
      setDescription(c.description);
      setConstraintType(c.constraintType);
      setFirstInstances([...c.firstInstances]);
      setSecondInstances([...c.secondInstances]);
      setSelectedGroupId(
        constraintDialog.groupId !== undefined
          ? String(constraintDialog.groupId)
          : "new"
      );
      setNewGroupName("");
      setErrorMessage(null);
    } else if (!open) {
      // Clear form state when dialog closes to avoid stale data on reopen
      setDescription("");
      setConstraintType("");
      setSelectedGroupId("new");
      setNewGroupName("");
      setFirstInstances([]);
      setSecondInstances([]);
      setErrorMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger on open/close, not on constraintDialog snapshot changes
  }, [open]);

  // Check if using single-instance mode
  const isSingleInstanceMode = SINGLE_INSTANCE_TYPES.has(constraintType);

  // Truncate arrays when switching to single-instance mode
  useEffect(() => {
    if (isSingleInstanceMode) {
      if (firstInstances.length > 1) {
        setFirstInstances([firstInstances[0]]);
      }
      if (secondInstances.length > 1) {
        setSecondInstances([secondInstances[0]]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger when mode changes, not when instances change
  }, [isSingleInstanceMode]);

  // Error message for validation failures
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Validate that all instances have the same symbol type
  const validateSymbolTypes = (): string | null => {
    const allInstances = [...firstInstances, ...secondInstances];
    if (allInstances.length === 0) return null;

    const types = new Set<string>();
    for (const name of allInstances) {
      const symbolType = instanceTypeMap.get(name);
      if (symbolType) {
        types.add(symbolType);
      }
    }

    // If we have more than one symbol type, it's invalid
    if (types.size > 1) {
      const typeList = [...types].join(", ");
      return `Cannot mix different device types in a symmetry constraint: ${typeList}`;
    }
    return null;
  };

  // Validation
  const hasFirstInstances = firstInstances.length > 0;
  const hasSecondInstances = secondInstances.length > 0;
  const symbolTypeError = validateSymbolTypes();
  const canSubmit =
    hasFirstInstances &&
    hasSecondInstances &&
    !isSubmitting &&
    constraintType &&
    !symbolTypeError;

  const handleSubmit = useCallback(async () => {
    if (!constraintDialog) return;

    // Clear previous error
    setErrorMessage(null);

    // symbolTypeError is already computed above - reuse it
    if (symbolTypeError) {
      setErrorMessage(symbolTypeError);
      return;
    }

    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const isCreate = constraintDialog.mode === "create";
      // For edit mode, constraint.id is always defined
      const constraintId = constraintDialog.constraint.id;

      // If "Create new group" is selected, create the group first
      let groupId: number | undefined;
      if (selectedGroupId === "new") {
        const groupDescription =
          newGroupName ||
          (constraintType ? constraintType.replace(/_/g, " ") : "new group");
        const groupResult = await workspace.addConstraintGroup({
          description: groupDescription,
        });
        if (groupResult.err) {
          setErrorMessage("Failed to create constraint group");
          return;
        }
        groupId = groupResult.val;
      } else {
        groupId = Number(selectedGroupId);
      }

      const result = isCreate
        ? await workspace.addConstraint({
            description: description || undefined,
            constraintType,
            firstInstances,
            secondInstances,
            groupId,
          })
        : constraintId !== undefined
        ? await workspace.updateConstraint(constraintId, {
            description: description || undefined,
            constraintType: constraintType || undefined,
            firstInstances,
            secondInstances,
            groupId,
          })
        : Result.Err({ why: "missing-constraint-id" });

      if (result.ok) {
        onClose();
      } else {
        // Show server-side error to user
        // Error shape from constraints.ts: { why: string, data?: string }
        const errorData = result.val as { why?: string; data?: string };
        const action = isCreate ? "add" : "update";
        setErrorMessage(
          errorData.data || errorData.why || `Failed to ${action} constraint`
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    constraintDialog,
    description,
    constraintType,
    selectedGroupId,
    newGroupName,
    firstInstances,
    secondInstances,
    symbolTypeError,
    workspace,
    onClose,
  ]);

  // Handle Enter key to submit
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && canSubmit) {
        e.preventDefault();
        void handleSubmit();
      }
    },
    [canSubmit, handleSubmit]
  );

  // Common styles
  const inputClass =
    "w-full rounded border px-2.5 py-1.5 text-xs font-mono outline-none";
  const inputStyle = {
    backgroundColor: "var(--color-ide-bg)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };
  const labelClass =
    "block text-[10px] font-semibold uppercase tracking-widest mb-1";
  const labelStyle = { color: "var(--color-ide-text-muted)" };

  const title =
    constraintDialog?.mode === "create" ? "Add Constraint" : "Edit Constraint";

  // Filter out already-selected instances for dropdowns
  const getAvailableFor = (side: "first" | "second"): string[] => {
    const otherSide = side === "first" ? secondInstances : firstInstances;
    const thisSide = side === "first" ? firstInstances : secondInstances;
    return availableInstances.filter(
      (name: string) => !otherSide.includes(name) && !thisSide.includes(name)
    );
  };

  // Compute options for single-instance selectors (include current value even if filtered)
  const getOptionsFor = (
    side: "first" | "second",
    value: string | undefined
  ): string[] => {
    const available = getAvailableFor(side);
    return value && !available.includes(value)
      ? [value, ...available]
      : available;
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="flex flex-col gap-3" onKeyDown={handleKeyDown}>
        {/* Constraint Type - shown first since it affects the rest of the UI */}
        <div>
          <label className={labelClass} style={labelStyle}>
            Type
          </label>
          <select
            value={constraintType}
            onChange={(e) => setConstraintType(e.target.value)}
            className={inputClass}
            style={inputStyle}
            autoFocus
          >
            <option value="">— Select type —</option>
            {CONSTRAINT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Group */}
        <div>
          <label className={labelClass} style={labelStyle}>
            Group
          </label>
          <select
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            className={inputClass}
            style={inputStyle}
          >
            <option value="new">Create new group</option>
            {availableGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.description ?? `Group ${g.id}`} (
                {g.symmetryDirection === "vertical" ? "vert" : "horiz"})
              </option>
            ))}
          </select>
        </div>

        {/* New group name - only shown when creating a new group */}
        {selectedGroupId === "new" && (
          <div>
            <label className={labelClass} style={labelStyle}>
              New Group Name
            </label>
            <input
              type="text"
              placeholder="e.g., Input pair"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className={inputClass}
              style={inputStyle}
            />
          </div>
        )}

        {/* Description */}
        <div>
          <label className={labelClass} style={labelStyle}>
            Description (optional)
          </label>
          <input
            type="text"
            placeholder="e.g., Input differential pair"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inputClass}
            style={inputStyle}
          />
        </div>

        {/* Instance selection - UI depends on constraint type */}
        {constraintType && (
          <>
            {isSingleInstanceMode ? (
              <>
                <SingleInstanceSelector
                  label="First Instance"
                  value={firstInstances[0]}
                  onChange={(v) => setFirstInstances(v ? [v] : [])}
                  options={getOptionsFor("first", firstInstances[0])}
                  formatInstance={formatInstance}
                  inputClass={inputClass}
                  inputStyle={inputStyle}
                  labelClass={labelClass}
                  labelStyle={labelStyle}
                />
                <SingleInstanceSelector
                  label="Second Instance"
                  value={secondInstances[0]}
                  onChange={(v) => setSecondInstances(v ? [v] : [])}
                  options={getOptionsFor("second", secondInstances[0])}
                  formatInstance={formatInstance}
                  inputClass={inputClass}
                  inputStyle={inputStyle}
                  labelClass={labelClass}
                  labelStyle={labelStyle}
                />
              </>
            ) : (
              <>
                <MultiInstanceSelector
                  label="First Group"
                  instances={firstInstances}
                  setInstances={setFirstInstances}
                  available={getAvailableFor("first")}
                  formatInstance={formatInstance}
                  inputClass={inputClass}
                  inputStyle={inputStyle}
                  labelClass={labelClass}
                  labelStyle={labelStyle}
                />
                <MultiInstanceSelector
                  label="Second Group"
                  instances={secondInstances}
                  setInstances={setSecondInstances}
                  available={getAvailableFor("second")}
                  formatInstance={formatInstance}
                  inputClass={inputClass}
                  inputStyle={inputStyle}
                  labelClass={labelClass}
                  labelStyle={labelStyle}
                />
              </>
            )}
          </>
        )}

        {/* Error message */}
        {(errorMessage || symbolTypeError) && (
          <div
            className="text-xs px-2 py-1.5 rounded"
            style={{
              backgroundColor:
                "var(--color-ide-error-bg, rgba(229, 62, 62, 0.1))",
              color: "var(--color-ide-error, #e53e3e)",
              border: "1px solid var(--color-ide-error, #e53e3e)",
            }}
          >
            {errorMessage || symbolTypeError}
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border"
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text-muted)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-xs rounded border"
            style={{
              backgroundColor: canSubmit
                ? "var(--color-ide-accent)"
                : "var(--color-ide-bg)",
              borderColor: canSubmit
                ? "var(--color-ide-accent)"
                : "var(--color-ide-border)",
              color: canSubmit ? "#fff" : "var(--color-ide-text-dim)",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
